/**
 * Window title bar: identity on the left, the case being worked on in the
 * middle, window controls on the right.
 *
 * `data-tauri-drag-region` makes the empty areas draggable in the real window
 * and is inert in the browser.
 */

import { Hexagon, Minus, Square, X } from "lucide-react";

import { BrandMark } from "@/shell/BrandMark";
import { useLayout } from "@/simple/layout";
import { useCurrentCase } from "@/store/appStore";
import { closeWindow, minimizeWindow, toggleMaximizeWindow } from "@/shell/window";
import { cn } from "@/lib/utils";

function ControlButton({
  label,
  onClick,
  danger,
  children,
}: {
  label: string;
  onClick: () => void;
  danger?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className={cn(
        "flex h-9 w-11 items-center justify-center text-ads-muted transition-colors duration-100",
        danger ? "hover:bg-red-600 hover:text-white" : "hover:bg-ads-hover hover:text-ads-text",
      )}
    >
      {children}
    </button>
  );
}

export function TitleBar() {
  const currentCase = useCurrentCase();

  return (
    <header
      data-tauri-drag-region
      className="flex h-9 shrink-0 items-center justify-between border-b border-ads-border bg-ads-surface pl-3 select-none"
    >
      <div data-tauri-drag-region className="flex items-center gap-2">
        <Hexagon size={14} className="text-ads-accent" strokeWidth={1.75} />
        <BrandMark />
      </div>

      <div data-tauri-drag-region className="flex flex-1 items-center justify-center gap-2 text-[12.5px]">
        {currentCase ? (
          <>
            <span className="font-medium text-ads-accent">{currentCase.subjectId}</span>
            <span className="text-ads-border-strong">|</span>
            <span className="text-ads-text">{currentCase.studyName}</span>
          </>
        ) : (
          <span className="text-ads-muted">No case open</span>
        )}
      </div>

      <div className="flex items-center">
        <button
          type="button"
          onClick={() => useLayout.getState().setMode("simple")}
          className="mr-2 rounded border border-ads-border px-2 py-0.5 text-[11px] text-ads-muted hover:bg-ads-hover hover:text-ads-text"
          title="Switch to the three-column workspace"
        >
          Simple view
        </button>
        <ControlButton label="Minimise" onClick={() => void minimizeWindow()}>
          <Minus size={14} />
        </ControlButton>
        <ControlButton label="Maximise" onClick={() => void toggleMaximizeWindow()}>
          <Square size={11} />
        </ControlButton>
        <ControlButton label="Close" danger onClick={() => void closeWindow()}>
          <X size={15} />
        </ControlButton>
      </div>
    </header>
  );
}
